import * as MSAL from '@citrite/msal-browser';
import { CtxNativeNetworkPostType } from '@citrite/msal-browser';
import { trackAnalyticsEvent } from 'analytics';
import { AxiosRequestConfig } from 'axios';
import { environment } from 'Environment/Environment';
import { AADSsoEventPayload } from 'Environment/msal/aadSSOEventPayload';
import {
	incompleteClientInfoError,
	isStoreUrlAndRedirectUriOriginMatch,
	shouldUseCustomSchemeForCustomerManagedClientId,
	validateClientInfo,
	wsuiAuthEnvironment,
} from 'Environment/msal/clientInfoUtils';
import { CtxStoreTicket } from 'staticPages/scripts/ctxStoreTicketUtils';
import { upnHandlerForNativeFlow } from 'Workspace/UserContext/upnHandlerForNative';

export const setAuthorizationCode = (aadAuthCode: MSAL.AADAuthCodeType) =>
	MSAL.setAuthorizationCode(aadAuthCode);

const onPerformAuthorization = async (
	authorizationProps: MSAL.MSALAuthorizationProps
) => {
	if (typeof environment.performAuthorizationV2 === 'function') {
		return await environment.performAuthorizationV2(JSON.stringify(authorizationProps));
	}
	return await environment.performAuthorization(JSON.stringify(authorizationProps));
};

const onAADResponse = (authResult: MSAL.AADAuthResult) =>
	environment.setAADResponse(JSON.stringify(authResult));

const onAADError = (error: MSAL.AADErrorResult) => {
	trackAnalyticsEvent(AADSsoEventPayload.tokenError());
	environment.setAADError(JSON.stringify(error));
};

// allow-unused-export : for unit test
export function getInteractionTypeFromRedirectUri(
	redirectUri: string
): MSAL.ctxAADInteractionType {
	//Customer managed client: redirect URIs start with ctxaadsso://
	if (redirectUri?.toLocaleLowerCase().startsWith(MSAL.ctxSchemeRedirectionURL)) {
		return MSAL.ctxAADInteractionType.REDIRECT_TO_CUSTOMER_MANAGED_CUSTOM_SCHEME;
	}

	//Customer managed client: redirect URIs start with https:// and match store URL origin
	if (isStoreUrlAndRedirectUriOriginMatch(redirectUri)) {
		return MSAL.ctxAADInteractionType.REDIRECT_TO_CUSTOMER_MANAGED_HTTPS;
	}

	//Citrix managed client: don't rely on redirect URI, use native capabilities
	if (environment.nativeCapabilities?.functions?.includes('performAuthorizationV2')) {
		return MSAL.ctxAADInteractionType.REDIRECT_TO_CTX_CUSTOM_SCHEME;
	}

	return MSAL.ctxAADInteractionType.REDIRECT_TO_CTX_HTTPS;
}

// allow-unused-export : for unit test
export const invokeNativeAjaxPost: CtxNativeNetworkPostType = async (
	tokenEndpoint: string,
	networkOptions: MSAL.NetworkRequestOptions
) => {
	try {
		MSAL.ctxConsoleLog(
			'ctxAjaxPostAsync called for tokenEndpoint:',
			tokenEndpoint,
			networkOptions
		);
		const ajaxPostOptions: AxiosRequestConfig = {
			method: 'POST',
			url: tokenEndpoint,
			data: networkOptions.body,
			headers: {
				...networkOptions.headers,
				'X-Citrix-Skip-Gateway': 'true', //This is critical to avoid going through gateway for native AAD calls
			},
			withCredentials: false,
		};
		const response = await environment.ajax(ajaxPostOptions);

		const successRet = {
			status: response.status,
			body: response.data,
			headers: response.headers,
		};

		successRet.body =
			typeof successRet.body === 'string' ? JSON.parse(successRet.body) : successRet.body;

		MSAL.ctxConsoleLog('ctxAjaxPostAsync SUCCESS and parsed body: ', successRet);
		return successRet;
	} catch (e) {
		MSAL.ctxConsoleLog('ctxAjaxPostAsync error:', e);
		const errorRet = {
			status: 500,
			body: { error: e?.message ?? 'unknown error' },
			headers: {},
		};
		return errorRet;
	}
};

export const initMsalSdk = () => {
	const initConfig: MSAL.MsalSdkInitConfig = {
		supportsInteractiveAuthOnly: environment.supportsInteractiveAuthOnly,
		interactionType: getInteractionTypeFromRedirectUri,
		onPerformAuthorization,
		onAADResponse,
		onAADError,
		environment: wsuiAuthEnvironment(),
		getCtxStoreTicket: async () => {
			return await CtxStoreTicket.request();
		},
	};
	MSAL.initMsalSdk(initConfig);
	return initConfig;
};

export const startAADTokenFlow = (authInfo: MSAL.AuthInfoWithCnf) => {
	if (!validateClientInfo(authInfo)) {
		onAADError(incompleteClientInfoError(authInfo.correlationId));
		return;
	}

	const invokeAADSSOCall = async () => {
		authInfo.loginHint = await upnHandlerForNativeFlow.getUpnForCurrentUser();
		trackAnalyticsEvent(AADSsoEventPayload.tokenRequested());

		// NOTE: this is the only place where we modify the redirect URI for customer managed client Id
		// Customer-managed client (custom scheme): use native network post for token call
		if (
			shouldUseCustomSchemeForCustomerManagedClientId(authInfo.redirectUri, environment)
		) {
			authInfo.redirectUri = MSAL.ctxSchemeRedirectionURL; //This is important since this gets sent to microsoft
			MSAL.oneTimeSetCtxNativeNetworkPostAsync(invokeNativeAjaxPost);
		}

		MSAL.startAADTokenFlow(authInfo);
	};

	invokeAADSSOCall();
};
